import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FiSave, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiMove,
  FiTarget, FiImage, FiEye
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default hotspots configuration (matching reference sites)
const DEFAULT_HOTSPOTS = {
  'livre-dor-video': {
    image: 'https://2026.justasmile-events.com/wp-content/uploads/2025/09/IMG_2393-1-683x1024.jpg',
    hotspots: [
      { id: 1, x: 50, y: 8, label: 'Caméra 4K', description: 'Permet un enregistrement de vidéo fluide avec une qualité 4K professionnelle', icon: '📹' },
      { id: 2, x: 25, y: 40, label: 'Micro col de cygne', description: 'Micro professionnel avec réducteur de bruit - notre micro capte uniquement votre voix', icon: '🎤' },
      { id: 3, x: 75, y: 35, label: 'Éclairage LED', description: 'Permet une luminosité adéquate pour un rendu optimal de vos vidéos', icon: '💡' },
      { id: 4, x: 50, y: 70, label: 'Pupitre élégant', description: 'Pour accompagner vos plus beaux messages avec un design soigné', icon: '🎨' },
    ]
  },
  'photobooth': {
    image: 'https://2025.justasmile-events.com/wp-content/uploads/2025/09/image00001-1-683x1024.jpeg',
    hotspots: [
      { id: 1, x: 70, y: 5, label: 'Flash Professionnel', description: 'Permet une luminosité et une qualité optimales, digne d\'un studio photo', icon: '⚡' },
      { id: 2, x: 72, y: 18, label: 'Appareil photo', description: 'Appareil photo professionnel Canon avec un objectif 18-55', icon: '📷' },
      { id: 3, x: 30, y: 22, label: 'Lumières LED', description: 'Éclairage LED professionnel pour vous mettre en lumière parfaitement', icon: '💡' },
      { id: 4, x: 55, y: 38, label: 'Écran tactile', description: 'Pour choisir vos modèles et voir l\'aperçu de vos photos en temps réel', icon: '📱' },
      { id: 5, x: 50, y: 75, label: 'Onduleur', description: 'Permet au PhotoBooth de ne pas s\'éteindre lors d\'un débranchement involontaire ou d\'une coupure de courant', icon: '🔋' },
    ]
  },
  'videobooth-360': {
    image: 'https://2025.justasmile-events.com/wp-content/uploads/2025/09/image00002-1-683x1024.jpeg',
    hotspots: [
      { id: 1, x: 25, y: 22, label: 'La ringlight', description: 'Une ringlight professionnelle', icon: '💡' },
      { id: 2, x: 40, y: 35, label: 'La caméra', description: 'Une caméra 4K de haute qualité pour des vidéos spectaculaires', icon: '🎥' },
      { id: 3, x: 50, y: 82, label: 'La plateforme', description: 'Une plateforme de 80cm ou 1 mètre de diamètre pouvant accueillir entre 3 et 5 personnes selon la taille choisie', icon: '🔄' },
      { id: 4, x: 75, y: 48, label: 'Lumières LED', description: 'Lumières LED multi-color & sans fils pour des effets visuels uniques', icon: '🌈' },
    ]
  }
};

// Emoji picker options
const EMOJI_OPTIONS = ['📷', '📹', '🎥', '🎤', '💡', '⚡', '🔋', '📱', '🎨', '🔄', '🌈', '✨', '🎯', '🖥️', '🎬', '🎵'];

export default function HotspotsConfigPage() {
  const [hotspotsConfig, setHotspotsConfig] = useState(DEFAULT_HOTSPOTS);
  const [activeProduct, setActiveProduct] = useState('photobooth');
  const [editingHotspot, setEditingHotspot] = useState(null);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragMode, setDragMode] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hotspots-config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setHotspotsConfig(data);
        }
      }
    } catch (error) {
      console.log('Using default hotspots config');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hotspots-config`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(hotspotsConfig)
      });
      if (response.ok) {
        toast.success('Configuration des hotspots enregistrée');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleImageClick = (e) => {
    if (!dragMode || !imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    if (selectedHotspot !== null) {
      // Move existing hotspot
      updateHotspot(selectedHotspot, 'x', Math.round(x));
      updateHotspot(selectedHotspot, 'y', Math.round(y));
      setSelectedHotspot(null);
      setDragMode(false);
      toast.success('Position mise à jour');
    } else {
      // Add new hotspot
      const newId = Math.max(...hotspotsConfig[activeProduct].hotspots.map(h => h.id), 0) + 1;
      const newHotspot = {
        id: newId,
        x: Math.round(x),
        y: Math.round(y),
        label: 'Nouveau point',
        description: 'Description du point',
        icon: '✨'
      };
      setHotspotsConfig(prev => ({
        ...prev,
        [activeProduct]: {
          ...prev[activeProduct],
          hotspots: [...prev[activeProduct].hotspots, newHotspot]
        }
      }));
      setEditingHotspot(newId);
      setDragMode(false);
      toast.success('Nouveau hotspot ajouté');
    }
  };

  const updateHotspot = (id, field, value) => {
    setHotspotsConfig(prev => ({
      ...prev,
      [activeProduct]: {
        ...prev[activeProduct],
        hotspots: prev[activeProduct].hotspots.map(h => 
          h.id === id ? { ...h, [field]: value } : h
        )
      }
    }));
  };

  const deleteHotspot = (id) => {
    setHotspotsConfig(prev => ({
      ...prev,
      [activeProduct]: {
        ...prev[activeProduct],
        hotspots: prev[activeProduct].hotspots.filter(h => h.id !== id)
      }
    }));
    toast.success('Hotspot supprimé');
  };

  const updateImage = (url) => {
    setHotspotsConfig(prev => ({
      ...prev,
      [activeProduct]: {
        ...prev[activeProduct],
        image: url
      }
    }));
  };

  const currentProduct = hotspotsConfig[activeProduct];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Configuration des Points Chauds</h1>
          <p className="text-gray-500">Gérez les hotspots sur les images de vos machines</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <FiSave className="mr-2 h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Product Tabs */}
      <Tabs value={activeProduct} onValueChange={setActiveProduct}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="photobooth">📷 PhotoBooth</TabsTrigger>
          <TabsTrigger value="videobooth-360">🎥 VideoBooth 360</TabsTrigger>
          <TabsTrigger value="livre-dor-video">🎤 Livre d'Or Vidéo</TabsTrigger>
        </TabsList>

        <TabsContent value={activeProduct} className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Image Preview with Hotspots */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FiImage className="h-5 w-5 text-amber-600" />
                    Aperçu & Position
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant={dragMode ? 'default' : 'outline'}
                      onClick={() => {
                        setDragMode(!dragMode);
                        setSelectedHotspot(null);
                      }}
                      className={dragMode ? 'bg-amber-600' : ''}
                    >
                      <FiPlus className="mr-1 h-4 w-4" />
                      {dragMode ? 'Mode Ajout Actif' : 'Ajouter'}
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  {dragMode 
                    ? selectedHotspot !== null 
                      ? 'Cliquez sur l\'image pour repositionner le hotspot sélectionné'
                      : 'Cliquez sur l\'image pour ajouter un nouveau hotspot' 
                    : 'Cliquez sur un hotspot pour le modifier, ou activez le mode Ajout'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Image URL */}
                <div className="mb-4">
                  <Label>URL de l'image</Label>
                  <Input 
                    value={currentProduct?.image || ''}
                    onChange={(e) => updateImage(e.target.value)}
                    placeholder="https://..."
                    className="mt-1 text-xs"
                  />
                </div>

                {/* Image with hotspots */}
                <div 
                  className={`relative rounded-lg overflow-hidden border-2 ${dragMode ? 'border-amber-400 cursor-crosshair' : 'border-transparent'}`}
                  onClick={handleImageClick}
                  ref={imageRef}
                >
                  <img 
                    src={currentProduct?.image} 
                    alt="Machine" 
                    className="w-full h-auto"
                  />
                  
                  {/* Hotspot points */}
                  {currentProduct?.hotspots.map((hotspot) => (
                    <button
                      key={hotspot.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (dragMode) {
                          setSelectedHotspot(hotspot.id);
                          toast.info('Cliquez sur l\'image pour repositionner');
                        } else {
                          setEditingHotspot(editingHotspot === hotspot.id ? null : hotspot.id);
                        }
                      }}
                      className={`absolute w-8 h-8 rounded-full flex items-center justify-center transition-all transform hover:scale-110 ${
                        editingHotspot === hotspot.id || selectedHotspot === hotspot.id
                          ? 'bg-amber-500 text-white scale-125 ring-4 ring-amber-300' 
                          : 'bg-black/80 text-white'
                      }`}
                      style={{ 
                        left: `${hotspot.x}%`, 
                        top: `${hotspot.y}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <span className="text-sm font-bold">{hotspot.id}</span>
                    </button>
                  ))}
                </div>

                {/* Position Guide */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <p><strong>💡 Astuce :</strong> Les positions sont en pourcentage (0-100). X = horizontal (gauche→droite), Y = vertical (haut→bas)</p>
                </div>
              </CardContent>
            </Card>

            {/* Hotspots List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FiTarget className="h-5 w-5 text-amber-600" />
                  Liste des Hotspots ({currentProduct?.hotspots.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
                {currentProduct?.hotspots.map((hotspot) => (
                  <motion.div 
                    key={hotspot.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 border rounded-lg ${
                      editingHotspot === hotspot.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{hotspot.icon}</span>
                        <div>
                          <Badge variant="outline">#{hotspot.id}</Badge>
                          <p className="font-medium mt-1">{hotspot.label}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedHotspot(hotspot.id);
                            setDragMode(true);
                            toast.info('Cliquez sur l\'image pour repositionner');
                          }}
                        >
                          <FiMove className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setEditingHotspot(editingHotspot === hotspot.id ? null : hotspot.id)}
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => deleteHotspot(hotspot.id)}
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Position display */}
                    <div className="flex gap-4 text-sm text-gray-500 mb-2">
                      <span>X: {hotspot.x}%</span>
                      <span>Y: {hotspot.y}%</span>
                    </div>

                    {/* Editing form */}
                    <AnimatePresence>
                      {editingHotspot === hotspot.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-3 mt-3 pt-3 border-t"
                        >
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Position X (%)</Label>
                              <Input 
                                type="number"
                                min={0}
                                max={100}
                                value={hotspot.x}
                                onChange={(e) => updateHotspot(hotspot.id, 'x', parseInt(e.target.value) || 0)}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Position Y (%)</Label>
                              <Input 
                                type="number"
                                min={0}
                                max={100}
                                value={hotspot.y}
                                onChange={(e) => updateHotspot(hotspot.id, 'y', parseInt(e.target.value) || 0)}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label>Titre</Label>
                            <Input 
                              value={hotspot.label}
                              onChange={(e) => updateHotspot(hotspot.id, 'label', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          
                          <div>
                            <Label>Description</Label>
                            <Textarea 
                              value={hotspot.description}
                              onChange={(e) => updateHotspot(hotspot.id, 'description', e.target.value)}
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                          
                          <div>
                            <Label>Icône</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {EMOJI_OPTIONS.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => updateHotspot(hotspot.id, 'icon', emoji)}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all ${
                                    hotspot.icon === emoji 
                                      ? 'bg-amber-500 ring-2 ring-amber-300' 
                                      : 'bg-gray-100 hover:bg-gray-200'
                                  }`}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <Button 
                            size="sm" 
                            onClick={() => setEditingHotspot(null)}
                            className="w-full bg-amber-600 hover:bg-amber-700"
                          >
                            <FiCheck className="mr-2 h-4 w-4" /> Terminé
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}

                {currentProduct?.hotspots.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FiTarget className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun hotspot configuré</p>
                    <p className="text-sm">Cliquez sur "Ajouter" puis sur l'image</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
